import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  aggregateRefKey,
  cellKey,
  columnHasNumericCells,
  type TableAggregateAxis,
  type TableAggregateOp,
} from '@/lib/tableForm'
import { FormFieldRefSelect, type RefFormGroup } from './FormFieldRefSelect'
import { TABLE_REF, tableRefKey, type TablePickMode, type TableSource } from './formulaRefPicker'

const MODE_LABELS: Record<TablePickMode, string> = {
  cell: 'One cell',
  'col:sum': 'Sum of a column',
  'col:avg': 'Average of a column',
  'row:sum': 'Sum of a row',
  'row:avg': 'Average of a row',
}

/**
 * Form → field picker, where a table counts as a field: picking one reveals
 * what to read from it. One cell is a column and a row (`{rowId__colId}`). A
 * table field also offers the sum or average of a number column or of a row
 * (`{tableId#col:colId:sum}`), which covers rows added at fill time too.
 * Used both to insert a reference and, seeded with an existing token, to
 * replace a chip in place.
 */
export function TableRefPicker({
  groups,
  tables,
  initial,
  cellAllowed,
  actionLabel,
  size,
  onPick,
  formula,
  owner,
}: {
  groups: RefFormGroup[]
  tables: TableSource[]
  initial: { ref: string; mode?: TablePickMode; colId?: string; rowId?: string }
  /** Which cells may be picked (by cell key): leaves out the cell itself, loops and unusable types. */
  cellAllowed: (key: string) => boolean
  actionLabel: string
  size?: 'default' | 'sm'
  onPick: (token: string) => void
  /** The formula so far, for suggesting fields that go with its operands. */
  formula: string
  /** The field whose formula this is, left out of the suggestions. */
  owner?: string
}) {
  const [picked, setPicked] = useState(initial.ref)
  const [mode, setMode] = useState<TablePickMode>(initial.mode ?? 'cell')
  const [col, setCol] = useState(initial.colId ?? '')
  const [row, setRow] = useState(initial.rowId ?? '')

  const table = picked.startsWith(TABLE_REF) ? tables.find(t => tableRefKey(t) === picked) : undefined
  // A grid with no field id has no total token.
  const offersTotals = !!table?.fieldId
  const [axis, op] = mode === 'cell' ? [null, null] : (mode.split(':') as [TableAggregateAxis, TableAggregateOp])

  const columns = table
    ? table.config.columns.filter(c =>
        axis === 'col'
          ? columnHasNumericCells(table.config, c.id)
          : table.config.rows.some(r => cellAllowed(cellKey(r.id, c.id))),
      )
    : []
  const rows = table
    ? table.config.rows.filter(r =>
        axis === 'row'
          ? true
          : col
            ? cellAllowed(cellKey(r.id, col))
            : table.config.columns.some(c => cellAllowed(cellKey(r.id, c.id))),
      )
    : []

  const token = !table
    ? ''
    : axis === 'col'
      ? col && `{${aggregateRefKey(table.fieldId, 'col', col, op!)}}`
      : axis === 'row'
        ? row && `{${aggregateRefKey(table.fieldId, 'row', row, op!)}}`
        : col && row && cellAllowed(cellKey(row, col))
          ? `{${cellKey(row, col)}}`
          : ''

  return (
    <div className='space-y-1.5'>
      <FormFieldRefSelect
        groups={groups}
        suggestFor={{ formula, owner }}
        value={picked}
        onChange={refKey => {
          if (refKey.startsWith(TABLE_REF)) {
            if (refKey !== picked) {
              setMode('cell')
              setCol('')
              setRow('')
            }
            setPicked(refKey)
            return
          }
          onPick(`{${refKey}}`)
          setPicked('')
        }}
        // Tables exist only on this form; leaving it drops the table pick so its
        // column/row selects don't linger beside another form's fields.
        onFormChange={formKey => formKey !== '' && setPicked('')}
        formPlaceholder='Pick a form…'
        fieldPlaceholder='Pick a field or table…'
        size={size}
      />
      {table && offersTotals && (
        <Select
          value={mode}
          onValueChange={v => {
            setMode(v as TablePickMode)
            setCol('')
            setRow('')
          }}
        >
          <SelectTrigger className='h-8 text-sm' aria-label='What to read'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(MODE_LABELS) as TablePickMode[]).map(m => (
              <SelectItem key={m} value={m}>
                {MODE_LABELS[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {table && (
        <div className='flex items-center gap-1.5'>
          {axis !== 'row' && (
            <Select
              value={col}
              onValueChange={v => {
                setCol(v)
                if (row && !cellAllowed(cellKey(row, v))) setRow('')
              }}
            >
              <SelectTrigger className='h-8 text-sm' aria-label='Column'>
                <SelectValue placeholder='Column' />
              </SelectTrigger>
              <SelectContent>
                {columns.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label || 'Untitled'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {axis !== 'col' && (
            <Select value={row} onValueChange={setRow}>
              <SelectTrigger className='h-8 text-sm' aria-label='Row'>
                <SelectValue placeholder='Row' />
              </SelectTrigger>
              <SelectContent>
                {rows.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label || 'Untitled'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-8 shrink-0'
            disabled={!token}
            onClick={() => token && onPick(token)}
          >
            {actionLabel}
          </Button>
        </div>
      )}
      {table && axis === 'col' && columns.length === 0 && (
        <p className='text-[11px] text-muted-foreground'>This table has no number columns to total.</p>
      )}
    </div>
  )
}
